/**
 * Remote console logger, for reading a headset's console from the desk.
 *
 * A Quest has no DevTools console attached during a walkthrough, so when
 * enabled this patches `console.*` and the global error/rejection handlers to
 * forward log lines to `POST api/remote-log` (a Cloudflare Pages Function;
 * another agent owns its server side). The URL is resolved against
 * `document.baseURI` rather than assumed absolute, because the build serves
 * from `base: "./"` and can sit under a sub-path.
 *
 * It is **opt-in and fully removable**: when disabled (the default) `console`
 * is left completely untouched, so there is zero overhead in a normal build.
 * Enable with the `VITE_REMOTE_LOG=1` build env var or the `?remote-log` URL
 * query param (handy on-device without a rebuild); `?remote-log=0` forces it
 * off even when the build was made with the env var set.
 *
 * Two channels, one contract. `?remote-log=https://…` sends the lines to a
 * relay named by the URL: the local console on the desk behind a Cloudflare
 * quick tunnel (`npm run log-relay -- --tunnel`), free and uncapped, which is
 * the default way to read a headset. Any other enabled value sends them to
 * the page's own Pages Function, which echoes to the deployment tail; that
 * costs one request per batch against the account's 100,000 Function
 * requests a day, so lines are **batched** (`REMOTE_LOG_FLUSH_MS`,
 * `REMOTE_LOG_FLUSH_LINES`) and **bounded**: `REMOTE_LOG_LIMITS` caps the
 * lines a second (the rest are dropped and summarised once a second) and the
 * lines a page load (after which the logger says so once and restores
 * `console`). The budget is pure and clock-injected, so it is unit tested
 * without a DOM. Nothing is stored on Cloudflare on either channel.
 *
 * `sessionId` is generated once per page load and shown on the Status window,
 * so a tester can read it out and the desk side of the session can find the
 * matching log stream.
 */
const ENDPOINT_PATH = "api/remote-log";

const LEVELS = ["log", "info", "warn", "error", "debug"] as const;
export type LogLevel = (typeof LEVELS)[number];

/** What one page load may put on the wire. */
export interface RemoteLogLimits {
  /** Lines admitted per second; the rest are dropped and summarised once a second. */
  perSecond: number;
  /** Lines (summaries included) per page load; then one stop line, and the logger switches off. */
  perSession: number;
}

/**
 * Worst case per page load is `perSession + 1` lines, and with batching far
 * fewer requests: about two percent of the account's daily Function budget on
 * the Workers Free plan even if every line went out on its own.
 */
export const REMOTE_LOG_LIMITS: RemoteLogLimits = { perSecond: 25, perSession: 2000 };

/** What a console line becomes on the wire, decided by the budget. */
export type BudgetAction = { kind: "line" } | { kind: "notice"; text: string } | { kind: "stop"; text: string };

/**
 * The per-page-load budget. `admit()` is called once per console line and
 * answers, in order, what to post: a notice about the lines dropped in the
 * previous second, the line itself (or nothing, when this second's allowance
 * is spent), and a final stop text once the session cap is reached. After
 * `stop` it answers nothing more. Notices count toward the session cap, so
 * the number of requests a page load can make is `perSession + 1`.
 */
export function createRemoteLogBudget(limits: RemoteLogLimits = REMOTE_LOG_LIMITS, now: () => number = () => Date.now()) {
  let sent = 0;
  let windowStart = now();
  let inWindow = 0;
  let dropped = 0;
  let stopped = false;

  // One more request goes out; reaching the session cap ends the session.
  const count = (out: BudgetAction[]): boolean => {
    sent += 1;
    if (sent >= limits.perSession) {
      stopped = true;
      out.push({ kind: "stop", text: `[RemoteLogger] session cap of ${limits.perSession} lines reached; remote logging is off for the rest of this page load` });
      return true;
    }
    return false;
  };

  return {
    admit(): BudgetAction[] {
      if (stopped) return [];
      const out: BudgetAction[] = [];
      const t = now();
      if (t - windowStart >= 1000) {
        windowStart = t;
        inWindow = 0;
        if (dropped > 0) {
          out.push({ kind: "notice", text: `[RemoteLogger] ${dropped} lines dropped in the last second (limit ${limits.perSecond} a second)` });
          dropped = 0;
          if (count(out)) return out;
        }
      }
      if (inWindow >= limits.perSecond) {
        dropped += 1;
        return out;
      }
      inWindow += 1;
      out.push({ kind: "line" });
      count(out);
      return out;
    },
    /** Requests this page load has put on the wire so far, notices included. */
    get sent(): number {
      return sent;
    },
    get stopped(): boolean {
      return stopped;
    },
  };
}

function makeSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID().slice(0, 8);
  } catch {
    /* crypto.randomUUID unavailable — fall through to Math.random */
  }
  return Math.random().toString(36).slice(2, 10);
}

/** One id per page load. Short, so it is easy to read aloud from the Status window. */
export const sessionId: string = makeSessionId();

/**
 * Whether remote logging should be active for this session.
 *
 * `search` defaults to the real page's query string; a caller can pass one
 * explicitly (as the tests do) to check the rule without a DOM. `?remote-log`
 * (present, any value but `"0"`) always wins, in either direction, over the
 * build-time env var — that is the escape hatch for turning it on or off
 * on-device without a rebuild.
 */
export function isRemoteLogEnabled(search: string = safeLocationSearch()): boolean {
  const param = new URLSearchParams(search).get("remote-log");
  if (param !== null) return param !== "0";
  try {
    if (import.meta.env?.VITE_REMOTE_LOG === "1") return true;
  } catch {
    /* import.meta.env unavailable in some runtimes — fall through */
  }
  return false;
}

/**
 * Where the lines go. `?remote-log=https://…` names a relay directly: the
 * local console on the desk reached through a Cloudflare quick tunnel
 * (`npm run log-relay -- --tunnel`), which costs nothing and has no daily
 * cap. Any other enabled value means the page's own Pages Function
 * (`api/remote-log`), which echoes to the deployment tail and counts one
 * request per batch against the account's daily Function budget.
 */
export function remoteLogEndpoint(search: string = safeLocationSearch()): string {
  const param = new URLSearchParams(search).get("remote-log") ?? "";
  if (/^https?:\/\//i.test(param)) return param;
  try {
    return new URL(ENDPOINT_PATH, document.baseURI).toString();
  } catch {
    return ENDPOINT_PATH;
  }
}

function safeLocationSearch(): string {
  try {
    return window.location.search;
  } catch {
    return "";
  }
}

/** Make a console argument JSON-safe (Errors/Events don't serialise usefully). */
export function serialize(arg: unknown): unknown {
  if (arg instanceof Error) return { $error: arg.message, stack: arg.stack };
  if (typeof Event !== "undefined" && arg instanceof Event) {
    return { $event: arg.type, target: String(arg.target) };
  }
  return arg;
}

/** Lines wait this long for company before they go out as one request. */
export const REMOTE_LOG_FLUSH_MS = 250;
/** A batch this long goes out at once. */
export const REMOTE_LOG_FLUSH_LINES = 25;

interface QueuedLine {
  level: LogLevel;
  args: unknown[];
  timestamp: string;
}

let endpoint = "";
let queue: QueuedLine[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
/** Requests put on the wire this page load; the perf monitor and the Status window read it. */
let requestsSent = 0;
/** Requests the network refused. A silent log channel is worse than none, so the Status window shows this. */
let requestsFailed = 0;

export function remoteLogRequests(): number {
  return requestsSent;
}

export function remoteLogFailures(): number {
  return requestsFailed;
}

/**
 * `fetch` with `keepalive`, not `sendBeacon`.
 *
 * A beacon's credentials mode is always "include", and a server answering a
 * cross-origin preflight with `Access-Control-Allow-Origin: *` must reject a
 * credentialed request — so every beacon to a relay on another origin failed,
 * silently, because `sendBeacon` only reports that it queued the bytes.
 * `keepalive` is what a beacon was for (a page that is unloading still
 * finishes the send) and omitting credentials keeps a wildcard relay usable.
 * The beacon stays as the fallback for a runtime without `fetch`.
 */
function send(body: string): void {
  requestsSent += 1;
  try {
    void fetch(endpoint, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      mode: "cors",
      credentials: "omit",
      keepalive: true,
    }).catch(() => {
      requestsFailed += 1;
    });
    return;
  } catch {
    /* fetch unavailable in this runtime — fall back to the beacon */
  }
  try {
    if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: "application/json" }))) return;
  } catch {
    /* blocked too */
  }
  requestsFailed += 1;
}

/** Put every queued line on the wire as one request. */
export function flushRemoteLog(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const lines = queue;
  queue = [];
  send(JSON.stringify({ session: sessionId, ua: typeof navigator !== "undefined" ? navigator.userAgent : "", lines }));
}

function post(level: LogLevel, args: unknown[]): void {
  queue.push({ level, args: args.map(serialize), timestamp: new Date().toISOString() });
  if (queue.length >= REMOTE_LOG_FLUSH_LINES) flushRemoteLog();
  else if (flushTimer === null) flushTimer = setTimeout(flushRemoteLog, REMOTE_LOG_FLUSH_MS);
}

/**
 * Activate remote logging if enabled. Returns a teardown that restores the
 * original console and removes the listeners — used by tests, and a no-op to
 * call in production. When logging is disabled this returns immediately
 * without touching anything, so callers can always call it unconditionally at
 * boot. The budget also calls that teardown itself once the session cap is
 * reached, so a runaway page stops costing requests and CPU.
 *
 * Console forwarding covers whatever the app already logs. On top of that,
 * `index.ts` calls plain `console.info(...)` at a handful of points (boot,
 * session start, placement transitions, the AR probe result, a frame-rate
 * sample every 10 s while presenting) so those facts reach the desk even if
 * nothing would otherwise have logged them.
 */
export function initRemoteLogger(options: { limits?: Partial<RemoteLogLimits>; now?: () => number } = {}): () => void {
  if (!isRemoteLogEnabled()) return () => {};

  const limits: RemoteLogLimits = { ...REMOTE_LOG_LIMITS, ...options.limits };
  const budget = createRemoteLogBudget(limits, options.now);
  endpoint = remoteLogEndpoint();

  // Capture the exact original references (no .bind) so teardown can restore
  // identity precisely and patching never stacks across sessions.
  const originals = LEVELS.map((level) => [level, console[level] as (...args: unknown[]) => void] as const);
  let patched = false;

  const forward = (level: LogLevel, args: unknown[]): void => {
    for (const action of budget.admit()) {
      if (action.kind === "line") post(level, args);
      else post("warn", [action.text]);
      if (action.kind === "stop") restore();
    }
  };
  const onError = (e: ErrorEvent) => forward("error", [`[uncaught] ${e.message}`, e.filename ? `@ ${e.filename}:${e.lineno}` : ""].filter(Boolean));
  const onRejection = (e: PromiseRejectionEvent) => {
    const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
    forward("error", [`[unhandled promise] ${reason}`]);
  };
  // A page that is hidden or unloading still gets its last lines out: the
  // beacon path is what survives an unload.
  const onHide = (): void => {
    if (document.visibilityState === "hidden") flushRemoteLog();
  };
  const restore = (): void => {
    if (!patched) return;
    patched = false;
    for (const [level, orig] of originals) console[level] = orig;
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", flushRemoteLog);
    flushRemoteLog();
  };

  for (const [level, orig] of originals) {
    console[level] = (...args: unknown[]) => {
      orig.apply(console, args);
      forward(level, args);
    };
  }
  patched = true;
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flushRemoteLog);

  console.log(`[RemoteLogger] Active (session ${sessionId}), forwarding to ${endpoint} in batches of up to ${REMOTE_LOG_FLUSH_LINES} lines every ${REMOTE_LOG_FLUSH_MS} ms; at most ${limits.perSecond} lines a second and ${limits.perSession} a page load`);

  return restore;
}
